<template>
  <div class="position-relative">
    <div v-if="loading" class="loading-overlay">
      <LoadingIndicator centered />
    </div>
    <table class="table">
      <thead class="table">
        <tr>
          <th class="px-0" scope="col"><b-form-checkbox @input="toggle" v-model="rowToggle" size="sm" class="my-0" /></th>
          <TableSortableHeader class="px-0 text-center" label="Id" field="id" />
          <TableSortableHeader :label="selectedStatus.label" :field="selectedStatus.sort" style="min-width: 120px;"/>
          <th scope="col">Customer</th>
          <th scope="col">Vehicles</th>
          <th scope="col">Route</th>
          <th scope="col">Carrier</th>
          <th scope="col">Price</th>
          <th scope="col">User</th>
          <th scope="col">Last updated</th>
        </tr>
      </thead>
      <tbody :class="{ 'table-loading': loading }" class="roboto">
        <tr v-for="lqo in items" :key="lqo.id">
          <td class="px-0">
            <b-form-checkbox v-model="selectedRows" :key="lqo.id" :value="lqo.id" size="sm" class="my-0" />
          </td>
          <td class="px-0 text-center"  :class="rowClassName(lqo)">
            <b-link :to="{ name: routeForIndividualItem, params: { id: lqo.id } }">#{{ lqo.id }}</b-link>
            <div class="text-tiny" v-for="task in lqo.tasksDue.filter(x => x.isFlag)" :key="task.id">{{ task.note }}</div>
          </td>
          <td :class="rowClassName(lqo)">
            {{ lqo[selectedStatus.sort_by] | formatDate }}
          </td>
          <td :class="rowClassName(lqo)">
            <div v-if="lqo.customer.contact.firstName || lqo.customer.contact.lastName">{{ lqo.customer.contact.firstName }} {{ lqo.customer.contact.lastName }}</div>
            <div v-if="lqo.customer.contact.company">{{ lqo.customer.contact.company }}</div>
          </td>
          <td :class="rowClassName(lqo)">
            <div style="display: flex; justify-content: space-between;">
              <div v-if="lqo.vehicles.length <= 2" style="width: fit-content;">
                <a v-for="vehicle in lqo.vehicles" :key="vehicle.id" :href="vehicleSearchLink(vehicle)" target="_blank" rel="noreferrer noopener" class="no-link-color d-block mb-1 d-flex align-items-center" style="gap: 4px;">
                  {{ vehicle.year }} {{ vehicle.make }} {{ vehicle.model }}
                  <BaseIcon v-if="vehicle.isInoperable" name="engine" :size="14" style="color: #BC1313;"/>
                </a>
              </div>
              <div v-else-if="lqo.vehicles.length > 2" class="d-flex align-items-center" style="gap: 4px;">
                Multiple Vehicles
                <BaseIcon v-if="lqo.vehicles.some(v => v.isInoperable)" name="engine" :size="14" style="color: #BC1313;"/>
              </div>

              <BaseIcon v-if="lqo.transportTypeId == 2" name="enclosed-trailer" :size="20" style="color: #BC1313;" />
            </div>
          </td>
          <td  :class="rowClassName(lqo)">
            <a href="#" @click="handleRouteClicked(lqo)" class="no-link-color route">
              <LeadlikeTableLocation :location="lqo.pickupLocation" />
              <span class="mr-1 fas fa-arrow-right"></span>
              <LeadlikeTableLocation :location="lqo.deliveryLocation" />
            </a>
          </td>
          <td  :class="rowClassName(lqo)">
            <div v-if="lqo.carrier">
              <b-link :to="{ path: `/carriers/view/${lqo.carrier.id}` }" class="no-link-color"> {{ lqo.carrier.contact.company }}</b-link>
            </div>
            <div v-else>Not Dispatched</div>
          </td>
          <td class="balance"  :class="rowClassName(lqo)">
            <div class="d-flex justify-content-between" style="gap: 4px;">
              <div>Tariff: </div>
              <div>${{ getBalance(lqo).tariff }}</div>
            </div>
            <div class="d-flex justify-content-between" style="gap: 4px;">
              <div>Deposit: </div>
              <div>${{ getBalance(lqo).deposit }}</div>
            </div>
            <div class="d-flex justify-content-between" style="gap: 4px;">
              <div>Cpay: </div>
              <div>${{ getBalance(lqo).carrierPay }}</div>
            </div>
          </td>
          <td  :class="rowClassName(lqo)">
            {{ lqo.user ? lqo.user.name : '-' }}
          </td>
          <td style="width: 95px;"  :class="rowClassName(lqo)">
            {{ lqo.updatedAt | formatRelativeTime }}
          </td>
        </tr>
      </tbody>
    </table>

    <RouteMapModal id="cardRouteModal" :route="routeInfo" />
  </div>
</template>

<script>
import TableSortableHeader from '@/components/shared/TableSortableHeader';
import LoadingIndicator from '@/components/shared/LoadingIndicator.vue';
import LeadlikeTableLocation from '@/components/shared/LeadlikeTableLocation';
import RouteMapModal from '@/components/shared/route/RouteMapModal';
import BaseIcon from '@/components/shared/BaseIcon.vue';

const orderStatuses = [
  { title: 'Orders', id: 3, sort: 'move_date', sort_by: 'moveDate', label: 'Move Date' },
  { title: 'Posted CD', id: 6, sort: 'move_date', sort_by: 'moveDate', label: 'Move Date' },
  { title: 'Not signed', id: 7, sort: 'dispatched_at', sort_by: 'dispatchedAt', label: 'Dispatched Date' },
  { title: 'Dispatched', id: 8, sort: 'signed_at', sort_by: 'signedAt', label: 'Signed Date' },
  { title: 'Picked-Up', id: 11, sort: 'pickup_at', sort_by: 'pickupAt', label: 'Picked Up' },
  { title: 'Delivered', id: 12, sort: 'delivery_at', sort_by: 'deliveryAt', label: 'Delivered Date' },
  { title: 'Issues', id: 9, sort: 'issues_at', sort_by: 'issuesAt', label: 'Reported Date' },
  { title: 'Hold', id: 4, sort: 'on_hold_At', sort_by: 'onHoldAt', label: 'Hold Date' },
  { title: 'Archive', id: 'archived', sort: 'deleted_at', sort_by: 'deletedAt', label: 'Archive Date' },
];

export default {
  name: 'LeadlikeTable',
  components: {
    LeadlikeTableLocation,
    LoadingIndicator,
    TableSortableHeader,
    RouteMapModal,
    BaseIcon,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    routeForIndividualItem: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    tablestatus: {
      type: [Number, String],
      default: 3,
    },
  },
  computed: {
    selectedStatus() {
      return this.statuses.find(s => s.id == this.tablestatus);
    },
  },
  data() {
    return {
      leadForRouteModal: null,
      statuses: orderStatuses,
      selectedRows: [],
      rowToggle: false,
      routeInfo: { id: null, origin: null, destination: null, infoHtml: null },
    };
  },
  methods: {
    rowClassName(lqo) {
      if(lqo.ndpOrderId) return 'bg-success-light';
      return lqo.tasksDue.length ? 'bg-red-light' : '';
    },
    toggle(checked) {
      if (checked) {
        this.selectedRows = this.items.map(x => x.id);
      } else {
        this.selectedRows = [];
      }
    },
    handleRouteClicked(lead) {
      const { id, pickupLocation, deliveryLocation } = lead;
      this.routeInfo = {
        id,
        origin: `${pickupLocation.city}, ${pickupLocation.state}, ${pickupLocation.zip}`,
        destination: `${deliveryLocation.city}, ${deliveryLocation.state}, ${deliveryLocation.zip}`,
        infoHtml: `(A) ${pickupLocation.city}, ${pickupLocation.state.toUpperCase()}, ${pickupLocation.zip}<i class="mx-1 fa-lg fas fa-shipping-fast"></i>(B) ${
          deliveryLocation.city
        }, ${deliveryLocation.state.toUpperCase()}, ${deliveryLocation.zip}`,
      };
      this.$bvModal.show('cardRouteModal');
    },
    vehicleSearchLink(vehicle) {
      return `https://www.google.com/search?tbm=isch&q=${vehicle.year} ${vehicle.make} ${vehicle.model}`;
    },
    getBalance(lqo) {
      return {
        tariff: lqo.vehicles?.map(v => v.price).reduce((total, p) => total + (parseFloat(p) || 0), 0) || 0,
        deposit: lqo.vehicles?.map(v => v.deposit).reduce((total, p) => total + (parseFloat(p) || 0), 0) || 0,
        carrierPay: parseInt(lqo.carrierPay) || 0,
      };
    }
  },
};
</script>

<style lang="scss" scoped>
tbody tr td:first-child {
  background-color: var(--bs-body-bg) !important;
}
.roboto {
  font-family: Roboto;
  font-weight: 400;
  font-size: 14px;
  line-height: 16.41px;
  letter-spacing: 0%;
}
.vehicle-link {
  display: block;
}
.table-loading {
  opacity: 0.5;
}
.loading-overlay {
  position: absolute;
  left: 50%;
  top: 50%;
}
tbody td {
  padding: 5px;
  /*min-height: 85px;*/
}
td {
  vertical-align: middle;
}
.balance {
  font-size: 12px;
}
.text-danger a, .text-success a {
  color: inherit;
}

.no-link-color {
  color: inherit !important;
  text-decoration: none !important;
}

.no-link-color:hover {
  color: gray !important;
  text-decoration: underline !important;
}
.route {
  display: grid;
  grid-template-columns: 1fr 24px 1fr;
  align-items: center;
}
</style>
